import type { Idl, Program } from '@coral-xyz/anchor';
import { createAssociatedTokenAccountIdempotentInstruction } from '@solana/spl-token';
import type { AccountMeta, Connection } from '@solana/web3.js';
import {
  ComputeBudgetProgram,
  PublicKey,
  type TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js';
import type BN from 'bn.js';
import { ASSOCIATED_TOKEN_PROGRAM_ID } from '../constants/programs.js';
import { deriveUsdcAta } from '../pda/ata.js';
import { deriveReferralLinkPda } from '../pda/derivations.js';
import type { BuildResult } from '../types/build-result.js';

// ─── Anchor method invocation helper ────────────────────────────────────────
// Required because noUncheckedIndexedAccess makes program.methods['name']
// return T | undefined. We do a runtime check and use a typed wrapper.

/** Anchor instruction argument primitives. */
type PrimitiveArg = BN | PublicKey | string | number | null | boolean | Uint8Array;

/** Anchor instruction argument type (primitives, arrays, nested arrays, or struct objects). */
type InstructionArg = PrimitiveArg | PrimitiveArg[] | PrimitiveArg[][] | Record<string, unknown>;

/** Chain returned by Anchor after calling .accounts(). */
interface AnchorAccountsChain {
  instruction(): Promise<TransactionInstruction>;
  remainingAccounts(accounts: AccountMeta[]): AnchorAccountsChain;
}

/** Chain returned by Anchor method builders. */
interface AnchorMethodChain {
  accounts(accs: Record<string, PublicKey | null>): AnchorAccountsChain;
}

/** Function signature for Anchor method builders. */
type MethodFn = (...args: InstructionArg[]) => AnchorMethodChain;

/** String-indexed record of method functions from program.methods. */
type MethodsRecord = Record<string, MethodFn | undefined>;

/**
 * Safely invoke an Anchor program instruction method by name.
 * Throws if the instruction doesn't exist in the program IDL.
 *
 * Usage:
 * ```ts
 * const ix = await invokeMethod(program, 'buy', amountIn, minTokensOut)
 *   .accounts({ user, market, ... })
 *   .instruction();
 * ```
 */
export function invokeMethod(
  program: Program<Idl>,
  name: string,
  ...args: InstructionArg[]
): AnchorMethodChain {
  const methods = program.methods as MethodsRecord;
  const fn = methods[name];
  if (!fn) {
    throw new Error(`Instruction "${name}" not found in program IDL`);
  }
  return fn(...args);
}

// ─── Interfaces ─────────────────────────────────────────────────────────────

/** Referral account set when a referrer is provided. */
export interface ReferralAccounts {
  referralLinkPda: PublicKey;
  referrerUsdcAta: PublicKey;
}

// ─── Build helpers ──────────────────────────────────────────────────────────

/**
 * Prepend a SetComputeUnitLimit instruction to the instruction array.
 * Always first instruction per FR-091.
 */
export function prependComputeBudget(
  instructions: TransactionInstruction[],
  computeUnits: number,
): TransactionInstruction[] {
  const cuIx = ComputeBudgetProgram.setComputeUnitLimit({ units: computeUnits });
  return [cuIx, ...instructions];
}

/**
 * Wrap instructions into a BuildResult with a VersionedTransaction.
 */
export function createBuildResult(
  instructions: TransactionInstruction[],
  signerKeys: PublicKey[],
  computeUnits: number,
): BuildResult {
  const allInstructions = prependComputeBudget(instructions, computeUnits);

  // VersionedTransaction with a dummy blockhash (caller refreshes before send)
  const message = new TransactionMessage({
    payerKey: PublicKey.default,
    recentBlockhash: '11111111111111111111111111111111',
    instructions: allInstructions,
  }).compileToV0Message();

  const transaction = new VersionedTransaction(message);

  return {
    transaction,
    signerKeys,
    instructions: allInstructions,
    computeUnits,
  };
}

export function uniqueSignerKeys(...keys: PublicKey[]): PublicKey[] {
  const seen = new Set<string>();
  const unique: PublicKey[] = [];
  for (const key of keys) {
    const base58 = key.toBase58();
    if (seen.has(base58)) continue;
    seen.add(base58);
    unique.push(key);
  }
  return unique;
}

/**
 * Create an idempotent ATA creation instruction.
 * Derives the correct associated token address from (owner, tokenProgram, mint)
 * before calling the @solana/spl-token instruction builder.
 */
export function createIdempotentAtaIx(
  payer: PublicKey,
  owner: PublicKey,
  mint: PublicKey,
  tokenProgram: PublicKey,
): TransactionInstruction {
  // Derive the ATA address: seeds = [owner, tokenProgramId, mint]
  const [associatedToken] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
  return createAssociatedTokenAccountIdempotentInstruction(
    payer,
    associatedToken,
    owner,
    mint,
    tokenProgram,
  );
}

/**
 * Resolve referral accounts if a referrer is provided.
 * Returns null if no referrer.
 */
export function resolveReferralAccounts(
  referrer: PublicKey | undefined,
  programId: PublicKey,
): ReferralAccounts | null {
  if (!referrer) {
    return null;
  }
  const [referralLinkPda] = deriveReferralLinkPda(referrer, programId);
  const referrerUsdcAta = deriveUsdcAta(referrer);
  return { referralLinkPda, referrerUsdcAta };
}

/**
 * Replace the blockhash in a VersionedTransaction with a fresh one from the cluster.
 * Used by the submission layer when a transaction expires before landing.
 */
export async function refreshBlockhash(
  connection: Connection,
  transaction: VersionedTransaction,
): Promise<VersionedTransaction> {
  const { blockhash } = await connection.getLatestBlockhash();
  transaction.message.recentBlockhash = blockhash;
  return transaction;
}
